import dayjs from "dayjs";
import { fileStatus, storyType, SupportedLanguageType } from "src/pages/Studies/models/interfaces";
import { validateLanguageInput, isNotesLikeStudy } from "src/pages/Studies/transformer";
import { environment } from "src/config/environment";

export enum PATTERN {
	ALPHANUMERIC = "^[a-zA-Z0-9]*$",
	STRICT = "_-~+()[]@?",
	FLEXIBLE = "[]()@!\\?~.,_-:'\"+&/|;*%^#$’–”“",
}

// Safe: static, non-user-controlled pattern (anchored char class, no
// backtracking risk). Compiled once and reused — not a ReDoS sink.
const ALPHANUMERIC_REGEX = new RegExp(PATTERN.ALPHANUMERIC);

export enum CHAR_LENGTH {
	XS = 10, // Extra small - codes, IDs
	S = 30, // Small - tags, labels
	M = 128, // Medium - titles, names
	L = 512, // Large - descriptions
	XL = 2048, // Extra large - content
	XXL = 4096, // Double XL - documents
	XXXL = 5000,
}

export const uploadTypes = {
	discussionGuide: {
		name: "Discussion Guide",
		title: "Upload",
		title2: "Reupload",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: docx",
		validTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".docx"],
		description: [
			"Optional but highly recommended.",
			"Upload only one Discussion Guide per study. For multiple Discussion Guides/sub-studies, please create new studies for each Discussion Guide.",
		],
		maxCount: 1,
		maxSize: 100 * 1024 * 1024, // 100MB
		multiple: false,
	},
	transcript: {
		name: "Transcript",
		title: "Upload Audio/Transcripts",
		title2: "Upload More Audio/Transcripts",
		title3: "Re-Upload Transcripts",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: docx, pdf, txt, vtt, audio*, video*",
		validTypes: [
			// Word documents
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			".docx",
			// Plain text
			"text/plain",
			".txt",
			// Audio formats
			"audio/amr",
			".amr",
			"audio/flac",
			".flac",
			"audio/x-m4a",
			".m4a",
			"audio/mpeg",
			".mp3",
			"video/mp4",
			".mp4",
			"audio/ogg",
			".ogg",
			".oga",
			"audio/opus",
			".opus",
			"audio/wav",
			".wav",
			"audio/webm",
			".webm",
			// "video/webm", // Not Supported currently
			// Newly added — keep in sync with backend ALLOWED_FILE_TYPES[transcript]
			"audio/mp4",
			".mp4a",
			"audio/aac",
			".aac",
			"audio/x-wma",
			"audio/x-ms-wma",
			".wma",
			// WebVTT subtitle files
			"text/vtt",
			".vtt",
			"application/pdf",
			".pdf",
		],
		description: ["Audio: amr, flac, m4a, mp3, ogg, opus, wav", "Video: mp4, webm", "Document: docx, pdf, txt, vtt"],
		maxCount: undefined,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: true,
	},
	report: {
		name: "Report",
		title: "Upload Reports",
		title2: "Upload More Reports",
		title3: "Re-Upload Reports",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: pptx, pdf",
		validTypes: [
			// PowerPoint presentations
			"application/vnd.openxmlformats-officedocument.presentationml.presentation",
			".pptx",
			// PDF files
			"application/pdf",
			".pdf",
		],
		description: ["Please upload at least one Report to proceed."],
		maxCount: undefined,
		maxSize: 200 * 1024 * 1024, // 200MB
		multiple: true,
	},
	metadata: {
		name: "Metadata",
		title: "Upload Metadata",
		title2: "Re-Upload Metadata",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted document types: xlsx, csv",
		validTypes: [
			// Excel spreadsheets
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			".xlsx",
			// CSV files
			"text/csv",
			".csv",
		],
		description: ["Please upload"],
		maxCount: 1,
		maxSize: 200 * 1024 * 1024, // 200MB
		multiple: false,
	},
	note: {
		name: "Notes",
		title: "Upload Raw Data",
		title2: "Reupload",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: xlsx, csv",
		validTypes: [
			// Excel spreadsheets
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			".xlsx",
			// CSV files
			"text/csv",
			".csv",
		],
		description: ["Please upload."],
		maxCount: 1,
		maxSize: 100 * 1024 * 1024, // 100MB
		multiple: false,
	},
	survey: {
		name: "Survey",
		title: "Upload Survey",
		title2: "Reupload",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted document types: docx",
		validTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".docx"],
		maxCount: 1,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: false,
		description: ["Filetype: docx"],
		description2: ["Upload a new or updated version of your survey . This will replace the current survey"],
	},
	rawData: {
		name: "Raw Data",
		title: "Upload Raw Data",
		title2: "Reupload",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: xlsx, csv, sav",
		validTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".xlsx", "text/csv", ".csv", "application/sav", ".sav"],
		maxCount: 1,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: false,
		description: ["Filetype: xlsx, csv, sav"],
		description2: ["Upload a new or updated version survey data file. This will overwrite the existing data"],
	},
	adm: {
		name: "Adm",
		title: "Upload Data Description",
		title2: "Reupload",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: xlsx",
		validTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".xlsx"],
		description: ["Filetype: xlsx"],
		maxCount: 1,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: false,
		description2: ["Upload an updated survey-map. This will replace the current file"],
	},
	analysis: {
		name: "Analysis Config",
		title: "Upload Analysis Config",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: xlsx",
		validTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".xlsx"],
		description: ["Accepted file types: xlsx"],
		maxCount: 1,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: false,
	},
	chart: {
		name: "Chart Config",
		title: "Upload Chart Config",
		uploadAction: `${environment.REACT_APP_API_URL}files/upload`,
		acceptedTypes: "Accepted file types: xlsx",
		validTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".xlsx"],
		description: ["Accepted file types: xlsx"],
		maxCount: 1,
		maxSize: 512 * 1024 * 1024, // 512MB
		multiple: false,
	},
};

export function getButtonStates(status, study?) {
	const study_type = study?.study_type || undefined;
	const study_language = study?.study_language;
	const buttonStates = {
		edit: false,
		delete: false,
		download: false,
		preview: false,
		equliaze: false,
	};

	switch (status?.toLowerCase()) {
		case fileStatus.queued:
			buttonStates.edit = false;
			buttonStates.delete = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = true;
			break;
		case fileStatus.uploading:
			buttonStates.edit = false;
			buttonStates.delete = false;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = false;
			break;
		case fileStatus.upload_failed:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = false;
			break;
		case fileStatus.processing:
			buttonStates.edit = false;
			buttonStates.delete = false;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = false;
			break;
		case fileStatus.preprocessing_failed:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = false;
			break;
		case fileStatus.success:
			buttonStates.edit = study_type === storyType.type1 ? (study_language?.code === SupportedLanguageType.English ? true : false) : true;
			buttonStates.delete = true;
			buttonStates.download = true;
			buttonStates.preview = true;
			buttonStates.equliaze = true;
			break;
		case fileStatus.warn:
			buttonStates.edit = study_type === storyType.type1 ? (study_language?.code === SupportedLanguageType.English ? true : false) : true;
			buttonStates.delete = true;
			buttonStates.download = true;
			buttonStates.preview = true;
			buttonStates.equliaze = true;
			break;
		case fileStatus.failed:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = isNotesLikeStudy(study_type) ? true : false;
			buttonStates.preview = false;
			buttonStates.equliaze = true;
			break;
		case fileStatus.validation_failed:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = false;
			buttonStates.preview = false;
			buttonStates.equliaze = true;
			break;
		case fileStatus.reupload_failed:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = false;
			buttonStates.preview = false;
			buttonStates.equliaze = true;
			break;
		case fileStatus.partial_processed:
			buttonStates.edit = true;
			buttonStates.delete = true;
			buttonStates.download = true;
			buttonStates.preview = true;
			buttonStates.equliaze = true;
			break;
		case fileStatus.uploaded:
			buttonStates.edit = false;
			buttonStates.delete = true;
			buttonStates.download = true;
			buttonStates.preview = false;
			buttonStates.equliaze = false;
			break;
		default:
			break;
	}

	return buttonStates;
}

export const metadataInfo = {
	metaDownload: "Download the metadata template to add additional information or tags to your uploaded transcripts. Detailed instructions are present in template.",
	metaUpload: "Upload your metadata file once you have downloaded the metadata template and filled the 'Metadata Table' sheet with transcript level tags.",
};

export const metadataReportsInfo = {
	metaDownload: "Download the metadata template to add additional information or tags to your uploaded reports. Detailed instructions are present in template.",
	metaUpload: "Upload your metadata file once you have downloaded the metadata template and filled the 'Metadata Table' sheet with reports level tags.",
};

export const studyTypeConfig = {
	[storyType.type1]: ["study_name", "folder_id", "study_language", "study_type_id", "geography_id", "fieldwork_date", "description", "project_code", "custom_tags", "process_video"],
	[storyType.type2]: ["study_name", "folder_id", "study_type_id", "geography_id", "description", "project_code", "custom_tags"],
	[storyType.type3]: ["study_name", "folder_id", "taxonomy_id", "study_type_id", "description", "project_code", "custom_tags", "template_id", "date_range"],
	[storyType.type6]: ["study_name", "folder_id", "taxonomy_id", "study_type_id", "description", "project_code", "custom_tags", "template_id", "date_range"],
	[storyType.type4]: ["study_name", "folder_id", "study_type_id", "geography_id", "description", "project_code", "custom_tags"],
};

const colSpanConfig = {
	[storyType.type1]: {
		study_name: 12,
		folder_id: 24,
		study_language: 12,
		study_type_id: 0,
		geography_id: 12,
		fieldwork_date: 12,
		description: 24,
		project_code: 12,
		custom_tags: 12,
		template_id: 0,
		process_video: 24,
	},
	[storyType.type2]: {
		study_name: 12,
		folder_id: 24,
		study_type_id: 0,
		geography_id: 12,
		fieldwork_date: 0,
		description: 24,
		project_code: 12,
		custom_tags: 12,
		template_id: 0,
	},
	[storyType.type3]: {
		study_name: 12,
		folder_id: 24,
		taxonomy_id: 12,
		study_type_id: 0,
		geography_id: 0,
		fieldwork_date: 0,
		description: 24,
		project_code: 12,
		custom_tags: 12,
		template_id: 24,
		date_range: 12,
	},
	[storyType.type6]: {
		study_name: 12,
		folder_id: 24,
		taxonomy_id: 12,
		study_type_id: 0,
		geography_id: 0,
		fieldwork_date: 0,
		description: 24,
		project_code: 12,
		custom_tags: 12,
		template_id: 24,
		date_range: 12,
	},
	[storyType.type4]: {
		study_name: 12,
		folder_id: 24,
		study_type_id: 0,
		geography_id: 12,
		fieldwork_date: 0,
		description: 24,
		project_code: 12,
		custom_tags: 12,
		template_id: 0,
	},
};

export const formConfig = (
	dropdownMaster,
	studiesData,
	editMode = false,
	currentStudyId,
	supportedLanguages,
	selectedStudyType?,
	allNotesTemplates?,
	allWebBoardsTemplates?,
	languages?,
	isMultiLingualSupportEnabled?,
	interfaceLanguage?,
	taxonomyDropdownData?,
	folderDropdownData?,
	isProjectCodeReq?,
	featureName?,
	isAudioVideoAvailable?: boolean,
	isAudioVideoBeta?: boolean,
) => {
	const baseConfig = [
		{
			filtername: "Study Language",
			rules: [{ required: true, message: "Study language is required." }],
			name: "study_language",
			type: "select",
			placeholder: "Select Language",
			options: languages || [],
			initialValue: interfaceLanguage?.code || SupportedLanguageType.English || "en",
			allowClear: true,
			disabled: editMode,
		},
		{
			filtername: "Study Name",
			name: "study_name",
			rules: [
				{ required: true, message: "Study name is required." },
				{
					validator: (_, value) => {
						if (!value) {
							return Promise.resolve();
						}
						if (value?.length > CHAR_LENGTH.M) {
							return Promise.reject(new Error(`Study name cannot exceed ${CHAR_LENGTH.M} characters.`));
						}

						const allowedSpecialChars = PATTERN.STRICT;
						const errorMessage = validateLanguageInput(supportedLanguages, value, allowedSpecialChars);
						if (errorMessage) {
							return Promise.reject(new Error(errorMessage + PATTERN.STRICT));
						}

						const studyNameExists = studiesData.some((study) => study.study_name.toLowerCase() === value.toLowerCase() && study.study_id !== currentStudyId);
						if (studyNameExists) {
							if (editMode) {
								const currentStudy = studiesData.find((study) => study.study_id === currentStudyId);
								if (currentStudy && currentStudy.study_name.toLowerCase() === value.toLowerCase()) {
									return Promise.resolve();
								}
							}
							return Promise.reject(new Error("A study with this name already exists."));
						}
						return Promise.resolve();
					},
				},
			],
			type: "input",
			placeholder: "Enter study name",
		},
		{
			filtername: "Taxonomy",
			name: "taxonomy_id",
			type: "dropdown",
			placeholder: "Select taxonomy",
			options: taxonomyDropdownData || [],
		},
		{
			filtername: "Study Type",
			name: "study_type_id",
			rules: [{ required: true, message: "Study type is required." }],
			type: "select",
			placeholder: "Select study type",
			options: dropdownMaster?.study_type || [],
			disabled: editMode,
		},
		{
			filtername: "Geography",
			name: "geography_id",
			type: "select",
			placeholder: "Select Geography",
			options: dropdownMaster?.geography || [],
			allowClear: true,
		},
		{
			filtername: "Fieldwork Date",
			name: "fieldwork_date",
			type: "dateRange",
			minDate: dayjs("01/01/2018", "MM/DD/YYYY"),
			// maxDate: dayjs("12/31/2025", "MM/DD/YYYY"),
			// maxYear: dayjs().endOf("year"),
			maxDate: dayjs().endOf("year"),
			dependencies: ["study_type_id"],
			condition: (getFieldValue) => {
				const selectedStudyType = dropdownMaster?.study_type.find((type: { value: string }) => type.value === getFieldValue("study_type_id"));
				return selectedStudyType?.label === storyType.type1;
			},
			allowClear: true,
		},
		{
			filtername: `${featureName ? featureName : "Folder"}`,
			name: "folder_id",
			type: "dropdown",
			placeholder: "Select folder",
			initialValue: null,
			rules: [
				{
					validator: async (_, value) => {
						if (value === undefined) {
							return Promise.reject(new Error("Folder is required."));
						}
						return Promise.resolve();
					},
				},
			],
			options: folderDropdownData || [],
		},
		{
			filtername: "Study Description",
			name: "description",
			rules: [
				{ required: true, message: "Study description is required." },
				{
					validator: (_, value) => {
						if (value && value?.length > CHAR_LENGTH.L) {
							return Promise.reject(new Error(`Study description cannot exceed ${CHAR_LENGTH.L} characters.`));
						}

						const allowedSpecialChars = PATTERN.FLEXIBLE;
						const errorMessage = validateLanguageInput(supportedLanguages, value, allowedSpecialChars);
						if (errorMessage) {
							return Promise.reject(new Error(errorMessage + PATTERN.FLEXIBLE));
						}
						return Promise.resolve();
					},
				},
			],
			type: "textarea",
			placeholder: "Enter study description",
			rows: 4,
			count: { show: true, max: CHAR_LENGTH.L },
		},
		{
			filtername: "Project Code",
			name: "project_code",
			rules: [
				...(isProjectCodeReq ? [{ required: true, message: "Project Code is required." }] : []),
				{
					validator: (_, value) => {
						if (value) {
							if (!value.trim()) {
								return Promise.reject(new Error("Input cannot be blank or spaces only"));
							}
							if (!ALPHANUMERIC_REGEX.test(value)) {
								return Promise.reject(new Error("Invalid language or special characters."));
							}

							if (value.length !== 10) {
								return Promise.reject(new Error("Project code must be 10 characters long."));
							}
						}
						return Promise.resolve();
					},
				},
			],
			type: "input",
			placeholder: "Enter project code",
		},
		{
			filtername: "Custom Tags",
			name: "custom_tags",
			type: "select",
			mode: "multiple",
			maxTagCount: "responsive",
			maxCount: 20,
			maxTagTextLength: CHAR_LENGTH.S,
			placeholder: "Please add or select tags",
			options: dropdownMaster?.custom_tags || [],
			infoTooltip:
				"Assign custom tags to your study to distinguish it from others or to group similar studies together. Useful examples include tagging by product names, research type, study objectives, and more.",
			rules: [
				{
					validator: async (_, value) => {
						if (value && value.length > 0) {
							const invalidTags = value.filter((tag) => {
								const validationError = validateLanguageInput(supportedLanguages, tag, PATTERN.STRICT);
								return validationError !== null;
							});
							if (invalidTags.length > 0) {
								return Promise.reject(new Error("Invalid language or special characters. Allowed:" + PATTERN.STRICT));
							}
						}
						return Promise.resolve();
					},
				},
			],
			allowClear: true,
		},
		{
			filtername: "Select Data Source",
			name: "template_id",
			type: "cardSelection",
			placeholder: "Select the type of data source",
			options: selectedStudyType?.label === storyType.type3 ? allNotesTemplates : allWebBoardsTemplates,
			rules: [{ required: true, message: "Please select a data source." }],
			dependencies: ["study_type_id"],
			disabled: editMode,
		},
		{
			filtername: "Date Range",
			name: "date_range",
			type: "dateRange",
			placeholder: "Select date range",
			minDate: dayjs("01/01/2000", "MM/DD/YYYY"),
			maxDate: dayjs().endOf("year"),
			allowClear: true,
		},
		{
			filtername: "Enable Video Processing",
			name: "process_video",
			type: "switch",
			valuePropName: "checked",
			initialValue: false,
			betaFeature: !!isAudioVideoBeta,
			disabled: editMode,
			dependencies: ["study_type_id"],
			condition: (getFieldValue) => {
				const selectedType = dropdownMaster?.study_type.find((type: { value: string }) => type.value === getFieldValue("study_type_id"));
				return selectedType?.label === storyType.type1 && !!isAudioVideoAvailable;
			},
		},
	];

	let colSpanMapping = colSpanConfig[selectedStudyType?.label] || {};

	if (selectedStudyType?.label === storyType.type1) {
		const dynamicWidth = isMultiLingualSupportEnabled ? 12 : 8;
		colSpanMapping = {
			...colSpanMapping,
			study_name: dynamicWidth,
			study_language: dynamicWidth,
			study_type_id: dynamicWidth,
			geography_id: dynamicWidth,
			fieldwork_date: dynamicWidth,
		};
	}

	return baseConfig
		.filter((field) => studyTypeConfig[selectedStudyType?.label]?.includes(field.name))
		.map((field) => ({
			...field,
			colSpan: colSpanMapping[field.name] ?? 6,
		}));
};

export function tabTooltipContent(tabKey, studyType = null) {
	switch (tabKey) {
		case "details":
			return "First, fill in the study details and add context (optional). Then, create iterations based on your research framework dates and upload your files to generate insights.";

		case "files":
			switch (studyType) {
				case storyType.type1:
					return "Start by uploading your Discussion Guide and Transcripts & Metadata files.";
				case storyType.type4:
					return "Start by uploading your survey and raw data file. The system will process it and generate a data description template for you to download, fill in, and re-upload to generate insights.";
				case storyType.type3:
					return "Start by uploading your raw data file for notes. The system will process it and generate insights.";
				case storyType.type6:
					return "Start by uploading your raw data file for web boards. The system will process it and generate insights.";
				case storyType.type2:
					return "Start by uploading your reports file. The system will process it and generate insights.";
				default:
					return null;
			}

		case "context":
			return "Enter the study's context here—objectives, competitor landscape, key assumptions, and any other relevant details to guide AI analysis.";

		case "configuration":
			return "Upload Survey, Raw Data and Mapping to enable.";

		default:
			return null;
	}
}

export const folderDeleteMessages: Record<number, string> = {
	1: "{featureName} will be deleted (no studies inside)",
	2: "{featureName} will be deleted and {total_studies_count} {total_studies_label} will be moved to home",
	3: "{featureName} and {studies_with_edit_access} {studies_with_edit_access_label} will be deleted.",
	4: "You don’t have full access to a few studies in this {featureName}, so you cannot delete this {featureName}."
	// 4: "You don't have full access to {studies_without_edit_access} {studies_without_edit_access_label} in this {featureName}",
	// 4: "You can’t delete this {featureName} because you don’t have edit access to all the studies it contains.",
};
